import { useId } from 'react';
import type { CSSProperties } from 'react';

export interface LogoProps {
  /** pixel box, applied to width & height (viewBox stays 64×64) */
  size?: number;
  className?: string;
  style?: CSSProperties;
  /** accessible name; also rendered as <title> */
  title?: string;
}

/**
 * The final TabVault mark (logo-stack.svg): stacked tabs sliding into a
 * vault-slot capped with the terracotta dash, on the landing's brand-mark
 * gradient. Inlined so it inherits no external asset dependency; the gradient
 * id is made unique per instance to avoid collisions when several render.
 */
export function Logo({ size = 32, className, style, title = 'TabVault' }: LogoProps) {
  const gradientId = `tv-logo-${useId()}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      className={className}
      style={style}
      role="img"
      aria-label={title}
    >
      <title>{title}</title>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#3B6BFF" />
          <stop offset=".48" stopColor="#1B3AE0" />
          <stop offset="1" stopColor="#0A0E5C" />
        </linearGradient>
      </defs>
      <rect width="64" height="64" rx="16" fill={`url(#${gradientId})`} />
      <rect x="15" y="13.5" width="34" height="8.5" rx="4.25" fill="#FFFFFF" opacity={0.45} />
      <rect x="15" y="26" width="34" height="8.5" rx="4.25" fill="#FFFFFF" opacity={0.75} />
      <rect x="13" y="38.5" width="38" height="12" rx="6" fill="#FFFFFF" />
      <rect x="24" y="42.5" width="16" height="4" rx="2" fill="#CC785C" />
    </svg>
  );
}

export interface WordmarkProps {
  /** wordmark font-size in px */
  size?: number;
  /** render the Logo mark before the text */
  showMark?: boolean;
  className?: string;
  style?: CSSProperties;
}

/** "TabVault" set in Inter Tight 700, −0.02em — optionally preceded by the mark. */
export function Wordmark({ size = 18, showMark = false, className, style }: WordmarkProps) {
  return (
    <span
      className={className}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, ...style }}
    >
      {showMark && <Logo size={Math.round(size * 1.25)} />}
      <span
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 700,
          fontSize: size,
          letterSpacing: '-.02em',
          color: 'var(--text-1)',
          lineHeight: 1,
        }}
      >
        TabVault
      </span>
    </span>
  );
}
